import { all, call, put, takeLatest } from 'redux-saga/effects';
import { handleError } from 'spw-redux/modules/UI/messages/actions';
import { APIResponse } from 'spw-redux/types';
import {
  getIndustries as getIndustriesApi,
  getClient as getClientApi,
  getClients as getClientsApi,
  createClient as createClientApi,
  deleteClient as deleteClientApi,
  updateClient as updateClientApi,
  getProjectsForClient,
} from '../../../api/clients';
import * as clientsActions from './actions';
import {
  ClientSlimForList,
  ClientListItemVM,
  IndustryListItemVM,
} from './types';

function* getClient(action: ReturnType<typeof clientsActions.getClient>) {
  try {
    const response: APIResponse<typeof getClientApi> = yield call(
      getClientApi,
      action.payload.clientId
    );

    yield put(clientsActions.actions.getClientSucceeded(response.data.client));
  } catch (e) {
    yield put(clientsActions.actions.getClientFailed());
    yield put(handleError(e));
  }
}

function* getClients(action: ReturnType<typeof clientsActions.getClients>) {
  try {
    const { sortBy, industryId, name, page, itemsPerPage } = action.payload;

    const response: APIResponse<typeof getClientsApi> = yield call(
      getClientsApi,
      page,
      itemsPerPage,
      sortBy,
      industryId,
      name
    );

    const clients = response.data.clients?.clients?.map(
      (client: ClientSlimForList) => ({
        ...client,
        projects: client.projects?.filter((proj) => !proj.archived),
      })
    ) as ClientListItemVM[];

    yield put(
      clientsActions.actions.getClientsSucceeded(clients, action.payload.sortBy)
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getIndustries(
  _action: ReturnType<typeof clientsActions.getIndustries>
) {
  try {
    const response: APIResponse<typeof getIndustriesApi> = yield call(
      getIndustriesApi
    );

    yield put(
      clientsActions.actions.getIndustriesSucceeded(
        response.data.industries as IndustryListItemVM[]
      )
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getProjectsForSelectedClient(
  action: ReturnType<typeof clientsActions.getProjectsForSelectedClient>
) {
  try {
    const { clientId, sort, page, itemsPerPage } = action.payload;
    const response: APIResponse<typeof getProjectsForClient> = yield call(
      getProjectsForClient,
      clientId,
      sort,
      page,
      itemsPerPage
    );

    const {
      data: {
        projects: { projects, total },
      },
    } = response;

    yield put(
      clientsActions.actions.getProjectsForSelectedClientSucceeded({
        projects,
        total,
      })
    );
  } catch (e) {
    yield put(handleError(e));
    yield put(clientsActions.actions.getProjectsForSelectedClientFailed());
  }
}

function* createClient(action: ReturnType<typeof clientsActions.createClient>) {
  try {
    const response: APIResponse<typeof createClientApi> = yield call(
      createClientApi,
      action.payload.form
    );

    yield put(
      clientsActions.actions.createClientSucceeded(response.data.client)
    );
  } catch (e) {
    yield put(clientsActions.actions.createClientFailed());
    yield put(handleError(e));
  }
}

function* deleteClient(action: ReturnType<typeof clientsActions.deleteClient>) {
  try {
    const response: APIResponse<typeof deleteClientApi> = yield call(
      deleteClientApi,
      action.payload.clientId
    );
    if (response.data.client.id) {
      yield put(clientsActions.actions.deleteClientSucceeded());
    }
  } catch (e) {
    yield put(clientsActions.actions.deleteClientFailed());
    yield put(handleError(e));
  }
}

function* updateClient(action: ReturnType<typeof clientsActions.updateClient>) {
  const {
    form: { clientId, image, industryId, name },
    onSuccess,
  } = action.payload;

  try {
    const response: APIResponse<typeof updateClientApi> = yield call(
      updateClientApi,
      clientId,
      image,
      industryId,
      name
    );

    if (onSuccess) yield call(onSuccess);
    yield put(
      clientsActions.actions.updateClientSucceeded(response.data.client)
    );
  } catch (e) {
    yield put(clientsActions.actions.updateClientFailed());
    yield put(handleError(e));
  }
}

function* saga() {
  yield all([
    takeLatest(clientsActions.GET_CLIENT, getClient),
    takeLatest(clientsActions.GET_CLIENTS, getClients),
    takeLatest(clientsActions.GET_INDUSTRIES, getIndustries),
    takeLatest(
      clientsActions.GET_PROJECTS_FOR_SELECTED_CLIENT,
      getProjectsForSelectedClient
    ),
    takeLatest(clientsActions.CREATE_CLIENT, createClient),
    takeLatest(clientsActions.DELETE_CLIENT, deleteClient),
    takeLatest(clientsActions.UPDATE_CLIENT, updateClient),
  ]);
}

export default saga;
